<div class="flex min-h-0 w-full min-w-0 flex-1 flex-col" [attr.data-testid]="'stock-history-cards-root'">
    @if (loading()) {
        <div
            class="flex min-w-0 flex-1 flex-col items-center justify-center gap-5 px-6 py-8 text-center text-slate-600 dark:text-slate-300"
            [attr.data-testid]="'stock-history-loading'"
        >
            <i
                class="fa-solid fa-spinner fa-spin text-5xl text-primary/35 dark:text-primary/40"
                aria-hidden="true"
            ></i>
            <p class="text-base font-medium">{{ 'common.loading' | translate }}</p>
        </div>
    } @else if (loadFailed()) {
        <div
            class="flex min-w-0 flex-1 flex-col items-center justify-center gap-5 px-8 py-12 text-center"
            role="alert"
            [attr.data-testid]="'stock-history-error'"
        >
            <i
                class="fa-solid fa-triangle-exclamation text-6xl text-primary/30 dark:text-primary/35"
                aria-hidden="true"
            ></i>
            <p class="max-w-sm text-base leading-relaxed text-slate-600 dark:text-slate-300">
                {{ 'document-line-item.stock-history.load-failed' | translate }}
            </p>
        </div>
    } @else if (isEmpty()) {
        <div
            class="flex min-w-0 flex-1 flex-col items-center justify-center gap-5 px-8 py-12 text-center"
            [attr.data-testid]="'stock-history-empty'"
        >
            <i
                class="fa-solid fa-box-open text-6xl text-primary/30 dark:text-primary/35"
                aria-hidden="true"
            ></i>
            <p class="max-w-sm text-base leading-relaxed text-slate-600 dark:text-slate-300">
                {{ emptyMessageKey() | translate }}
            </p>
        </div>
    } @else {
        <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto py-1">
            @for (item of displayItems(); track item.trackId) {
                @if (item.kind === 'single') {
                    @let tx = item.tx;
                    <article
                        class="min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-900"
                        [attr.data-testid]="'stock-history-card-' + tx.id"
                    >
                        <div class="px-4 py-3">
                            <div class="flex min-w-0 items-center justify-between gap-3">
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="break-words text-base font-semibold text-slate-900 dark:text-slate-100"
                                        [attr.title]="productDisplay(tx)"
                                    >
                                        {{ productDisplay(tx) }}
                                    </p>
                                    <div
                                        class="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-normal leading-none text-slate-600 dark:text-slate-400"
                                    >
                                        <span>{{ quantityNumber(tx) }}</span>
                                        @if (quantityUnit(tx); as unit) {
                                            <span>{{ unit }}</span>
                                        }
                                        @if (productIdentifierInline(tx); as productId) {
                                            <span
                                                class="text-slate-400 dark:text-slate-500"
                                                aria-hidden="true"
                                                >·</span
                                            >
                                            <span
                                                class="break-words"
                                                [attr.title]="productId"
                                                [attr.data-testid]="
                                                    'stock-history-card-product-identifier-' + tx.id
                                                "
                                            >
                                                {{ productId }}
                                            </span>
                                        }
                                        @if (identificationInline(tx); as idLine) {
                                            <span
                                                class="text-slate-400 dark:text-slate-500"
                                                aria-hidden="true"
                                                >·</span
                                            >
                                            <span
                                                class="break-words"
                                                [attr.title]="idLine"
                                                [attr.data-testid]="
                                                    'stock-history-card-identification-' + tx.id
                                                "
                                            >
                                                {{ idLine }}
                                            </span>
                                        }
                                        @if (showCardValue(tx)) {
                                            <span
                                                class="text-slate-400 dark:text-slate-500"
                                                aria-hidden="true"
                                                >·</span
                                            >
                                            <span
                                                [attr.data-testid]="
                                                    'stock-history-card-value-' + tx.id
                                                "
                                            >
                                                {{
                                                    tx.value
                                                        | localeCurrency: currencyCode() : 0.01
                                                }}
                                            </span>
                                        }
                                        @if (tx.expirationDate) {
                                            <span
                                                class="text-slate-400 dark:text-slate-500"
                                                aria-hidden="true"
                                                >·</span
                                            >
                                            <span
                                                [attr.title]="formatExpiryLong(tx)"
                                                [attr.data-testid]="
                                                    'stock-history-card-expiry-' + tx.id
                                                "
                                            >
                                                {{ formatExpiryInline(tx) }}
                                            </span>
                                        }
                                    </div>
                                </div>
                                <div
                                    class="shrink-0 self-center"
                                    [attr.data-testid]="'stock-history-card-actor-' + tx.id"
                                >
                                    @if (hasTransactionEmployee(tx)) {
                                        <phx-avatar
                                            [employee]="transactionEmployee(tx)"
                                            [tooltip]="actorDisplay(tx)"
                                            size="small"
                                            shape="square"
                                        />
                                    } @else {
                                        <span
                                            class="flex h-[22.5px] w-[22.5px] items-center justify-center rounded text-slate-400 dark:text-slate-500"
                                            [pTooltip]="actorDisplay(tx)"
                                            [attr.aria-label]="actorDisplay(tx)"
                                        >
                                            <i
                                                class="fa-regular fa-user text-sm"
                                                aria-hidden="true"
                                            ></i>
                                        </span>
                                    }
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-slate-200 px-4 py-3 dark:border-slate-600"
                            [attr.data-testid]="'stock-history-card-logistics-' + tx.id"
                        >
                            <div
                                class="flex min-w-0 items-center"
                                [class.gap-3]="showStorageRoute(tx)"
                                [attr.data-testid]="
                                    showStorageRoute(tx)
                                        ? 'stock-history-card-route-' + tx.id
                                        : null
                                "
                            >
                                @if (showStorageRoute(tx)) {
                                    <div
                                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                                        [ngClass]="storageRouteIconClasses(tx)"
                                    >
                                        <i [class]="storageRouteIcon(tx)" aria-hidden="true"></i>
                                    </div>
                                }
                                <div class="flex min-w-0 flex-1 flex-col gap-1">
                                    @if (showStorageRoute(tx)) {
                                        @if (isRelocate(tx)) {
                                            <div
                                                class="flex flex-col gap-1"
                                                [attr.title]="storageRouteLine(tx)"
                                            >
                                                <p
                                                    class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                >
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-from'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.originStoragePlace)
                                                    }}</span>
                                                </p>
                                                <p
                                                    class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                >
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-to'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.targetStoragePlace)
                                                    }}</span>
                                                </p>
                                            </div>
                                        } @else {
                                            <p
                                                class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                [attr.title]="storageRouteLine(tx)"
                                            >
                                                @if (isOutbound(tx)) {
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-from'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.originStoragePlace)
                                                    }}</span>
                                                } @else {
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-to'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.targetStoragePlace)
                                                    }}</span>
                                                }
                                            </p>
                                        }
                                    }
                                    <p
                                        class="text-sm text-slate-500 dark:text-slate-400"
                                        [attr.data-testid]="'stock-history-card-date-' + tx.id"
                                    >
                                        {{ formatTransactionTime(tx) }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        @if (hasNotes(tx)) {
                            <div
                                class="border-t border-slate-200 px-4 py-3 dark:border-slate-600"
                                [attr.data-testid]="'stock-history-card-notes-' + tx.id"
                            >
                                <p
                                    class="break-words whitespace-pre-wrap text-sm text-slate-900 dark:text-slate-100"
                                >
                                    {{ tx.notes!.trim() }}
                                </p>
                            </div>
                        }
                    </article>
                } @else {
                    @let group = item;
                    @let tx = group.representativeTx;
                    <article
                        class="min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-900"
                        [attr.data-testid]="'stock-history-group-' + group.trackId"
                    >
                        <div class="px-4 py-3">
                            <div class="flex min-w-0 items-center justify-between gap-3">
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="break-words text-base font-semibold text-slate-900 dark:text-slate-100"
                                        [attr.title]="productDisplay(tx)"
                                    >
                                        {{ productDisplay(tx) }}
                                    </p>
                                    <div
                                        class="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-normal leading-none text-slate-600 dark:text-slate-400"
                                    >
                                        <span>{{ groupTotalQuantity(group) }}</span>
                                        @if (groupQuantityUnit(group); as unit) {
                                            <span>{{ unit }}</span>
                                        }
                                        @if (productIdentifierInline(tx); as productId) {
                                            <span
                                                class="text-slate-400 dark:text-slate-500"
                                                aria-hidden="true"
                                                >·</span
                                            >
                                            <span
                                                class="break-words"
                                                [attr.title]="productId"
                                                [attr.data-testid]="
                                                    'stock-history-card-product-identifier-' + tx.id
                                                "
                                            >
                                                {{ productId }}
                                            </span>
                                        }
                                    </div>
                                </div>
                                <div class="flex shrink-0 items-center gap-2 self-center">
                                    <button
                                        type="button"
                                        class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-1 py-0 text-sm font-semibold text-primary hover:text-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                        [attr.data-testid]="
                                            'stock-history-group-toggle-' + group.trackId
                                        "
                                        [attr.aria-expanded]="isGroupExpanded(group.trackId)"
                                        (click)="toggleGroupExpanded(group.trackId)"
                                    >
                                        @if (isGroupExpanded(group.trackId)) {
                                            {{
                                                'document-line-item.stock-history.hide-details'
                                                    | translate
                                            }}
                                        } @else {
                                            {{
                                                'document-line-item.stock-history.show-items'
                                                    | translate
                                                        : { count: group.transactions.length }
                                            }}
                                        }
                                        <i
                                            class="text-xs"
                                            [ngClass]="groupToggleIcon(group.trackId)"
                                            aria-hidden="true"
                                        ></i>
                                    </button>
                                    <div
                                        class="shrink-0 self-center"
                                        [attr.data-testid]="'stock-history-card-actor-' + tx.id"
                                    >
                                        @if (hasTransactionEmployee(tx)) {
                                            <phx-avatar
                                                [employee]="transactionEmployee(tx)"
                                                [tooltip]="actorDisplay(tx)"
                                                size="small"
                                                shape="square"
                                            />
                                        } @else {
                                            <span
                                                class="flex h-[22.5px] w-[22.5px] items-center justify-center rounded text-slate-400 dark:text-slate-500"
                                                [pTooltip]="actorDisplay(tx)"
                                                [attr.aria-label]="actorDisplay(tx)"
                                            >
                                                <i
                                                    class="fa-regular fa-user text-sm"
                                                    aria-hidden="true"
                                                ></i>
                                            </span>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-slate-200 px-4 py-3 dark:border-slate-600"
                            [attr.data-testid]="'stock-history-card-logistics-' + tx.id"
                        >
                            @if (showStorageRoute(tx)) {
                                <div
                                    class="flex min-w-0 items-center gap-3"
                                    [attr.data-testid]="'stock-history-card-route-' + tx.id"
                                >
                                    <div
                                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                                        [ngClass]="storageRouteIconClasses(tx)"
                                    >
                                        <i [class]="storageRouteIcon(tx)" aria-hidden="true"></i>
                                    </div>
                                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                                        @if (isRelocate(tx)) {
                                            <div
                                                class="flex flex-col gap-1"
                                                [attr.title]="storageRouteLine(tx)"
                                            >
                                                <p
                                                    class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                >
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-from'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.originStoragePlace)
                                                    }}</span>
                                                </p>
                                                <p
                                                    class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                >
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-to'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.targetStoragePlace)
                                                    }}</span>
                                                </p>
                                            </div>
                                        } @else {
                                            <p
                                                class="break-words text-sm text-slate-900 dark:text-slate-100"
                                                [attr.title]="storageRouteLine(tx)"
                                            >
                                                @if (isOutbound(tx)) {
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-from'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.originStoragePlace)
                                                    }}</span>
                                                } @else {
                                                    <span
                                                        class="font-normal text-slate-500 dark:text-slate-400"
                                                        >{{
                                                            'document-line-item.stock-history.route-to'
                                                                | translate
                                                        }}:
                                                    </span>
                                                    <span class="font-semibold">{{
                                                        storagePlaceSummary(tx.targetStoragePlace)
                                                    }}</span>
                                                }
                                            </p>
                                        }
                                        <p
                                            class="text-sm text-slate-500 dark:text-slate-400"
                                            [attr.data-testid]="
                                                'stock-history-card-date-' + tx.id
                                            "
                                        >
                                            {{ formatTransactionTime(tx) }}
                                        </p>
                                    </div>
                                </div>
                            } @else {
                                <p
                                    class="text-sm text-slate-500 dark:text-slate-400"
                                    [attr.data-testid]="'stock-history-card-date-' + tx.id"
                                >
                                    {{ formatTransactionTime(tx) }}
                                </p>
                            }

                            @if (isGroupExpanded(group.trackId)) {
                                <ul
                                    class="mt-2 overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-900"
                                    role="list"
                                >
                                    @for (
                                        row of group.transactions;
                                        track row.id;
                                        let last = $last
                                    ) {
                                        <li
                                            class="flex min-w-0 items-center justify-between gap-3 px-3 py-1.5 text-sm"
                                            [class.border-b]="!last"
                                            [class.border-slate-200]="!last"
                                            [class.dark:border-slate-600]="!last"
                                            [attr.data-testid]="
                                                'stock-history-group-row-' + row.id
                                            "
                                        >
                                            <div
                                                class="flex min-w-0 items-center gap-x-1.5 truncate font-normal text-slate-600 dark:text-slate-400"
                                            >
                                                <span class="shrink-0">{{
                                                    quantityNumber(row)
                                                }}</span>
                                                @if (quantityUnit(row); as rowUnit) {
                                                    <span class="shrink-0">{{ rowUnit }}</span>
                                                }
                                                @if (identificationInline(row); as idLine) {
                                                    <span
                                                        class="shrink-0 text-slate-400 dark:text-slate-500"
                                                        aria-hidden="true"
                                                        >·</span
                                                    >
                                                    <span
                                                        class="truncate"
                                                        [attr.title]="idLine"
                                                    >
                                                        {{ idLine }}
                                                    </span>
                                                }
                                            </div>
                                            <div
                                                class="flex shrink-0 items-center gap-x-1.5 font-normal text-slate-600 dark:text-slate-400"
                                            >
                                                @if (showCardValue(row)) {
                                                    <span class="shrink-0">
                                                        {{
                                                            row.value
                                                                | localeCurrency
                                                                    : currencyCode()
                                                                    : 0.01
                                                        }}
                                                    </span>
                                                    <span
                                                        class="shrink-0 text-slate-400 dark:text-slate-500"
                                                        aria-hidden="true"
                                                        >·</span
                                                    >
                                                }
                                                <span
                                                    class="shrink-0 text-slate-500 dark:text-slate-400"
                                                    [attr.data-testid]="
                                                        'stock-history-group-row-date-' +
                                                        row.id
                                                    "
                                                >
                                                    {{ formatTransactionTime(row) }}
                                                </span>
                                            </div>
                                        </li>
                                    }
                                </ul>
                            }
                        </div>
                    </article>
                }
            }
        </div>
    }
</div>
